import { Button, FormField, Modal, Select, useToast } from "@design";
import { useMutation } from "@tanstack/react-query";
import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AiModelResponse, adminReassignModelMutation, errorMessage, type ModelReferencesResponse } from "@/api";
import { Text } from "@/components/Text";
import { type ModelType, replacementCandidates } from "./modelOptions";
import { useDescribeUsage } from "./useDescribeUsage";

export type ReassignAction = "delete" | "disable" | "move";

export function ReassignDialog({
  model,
  models,
  action,
  references,
  onClose,
  onDone,
}: Readonly<{
  model: AiModelResponse;
  models: AiModelResponse[];
  action: ReassignAction;
  references: ModelReferencesResponse;
  onClose: () => void;
  onDone: () => void;
}>) {
  const { t } = useTranslation("adminModels");
  const toast = useToast();
  const describeUsage = useDescribeUsage();
  const selectId = useId();
  const candidates = replacementCandidates(model, models);
  const [target, setTarget] = useState(() => candidates.find((m) => m.id === model.replacementModelId)?.id ?? "");
  const reassign = useMutation(adminReassignModelMutation());
  const agents = references.affectedAgents ?? [];
  const chats = references.chatCount ?? 0;
  const inUse = agents.length > 0 || chats > 0;

  const move = () =>
    reassign.mutate(
      { path: { model_id: model.id }, body: { replacementModelId: target } },
      {
        onSuccess: (res) => {
          toast.success(res.message);
          onDone();
        },
        onError: (error) => toast.error(errorMessage(error, t("reassign.failed"))),
      },
    );

  return (
    <Modal
      open
      onClose={onClose}
      title={t(`reassign.title.${action}`, { name: model.name })}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={reassign.isPending}>
            {t("reassign.cancel")}
          </Button>
          {inUse ? (
            <Button
              variant={action === "delete" ? "danger" : "primary"}
              disabled={!target}
              loading={reassign.isPending}
              onClick={move}
            >
              {t(`reassign.moveAction.${action}`)}
            </Button>
          ) : (
            <Button variant="danger" onClick={onDone}>
              {t("reassign.deleteAction")}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {inUse ? (
          <>
            <Text variant="body-sm" tone="secondary">
              {t("reassign.inUse", { name: model.name, usage: describeUsage(agents.length, chats) })}{" "}
              {t(`reassign.nextStep.${action}`)}
            </Text>
            {agents.length > 0 && (
              <ul
                aria-label={t("reassign.agentsLabel")}
                className="flex max-h-32 flex-col gap-1 overflow-y-auto rounded-lg border border-border-subtle px-3 py-2"
              >
                {agents.map((agent) => (
                  <li key={agent.uuid}>
                    <Text as="span" variant="body-sm">
                      {agent.name}
                    </Text>
                  </li>
                ))}
              </ul>
            )}
            {candidates.length > 0 ? (
              <FormField label={t("reassign.moveTo")} htmlFor={selectId}>
                <Select
                  id={selectId}
                  aria-label={t("reassign.moveTo")}
                  placeholder={t("reassign.selectModel")}
                  value={target || undefined}
                  options={candidates.map((m) => ({ value: m.id, label: m.name }))}
                  onChange={setTarget}
                />
              </FormField>
            ) : (
              <Text variant="body-sm" tone="warning">
                {t("reassign.noCandidates", { type: t(`modelTypes.${(model.modelType ?? "chat") as ModelType}`) })}
              </Text>
            )}
          </>
        ) : (
          <Text variant="body-sm" tone="secondary">
            {t("reassign.confirmDelete", { name: model.name })}
          </Text>
        )}
        {action === "delete" && (
          <Text variant="body-sm" tone="tertiary">
            {t("reassign.deleteWarning")}
          </Text>
        )}
      </div>
    </Modal>
  );
}
